import React, { useState, useEffect, useCallback, useRef } from 'react';
import { KeyRound, Plus, Trash2, Loader2, ShieldCheck, Monitor } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getUserPasskeys, deletePasskey } from '../_services/passkey.service';
import PasskeyRegisterPanel from '@/components/PasskeyRegisterPanel';

/**
 * Passkey management dialog.
 * Opened from the Tools page for each role.
 *
 * Props:
 *   userId   {number}   — user.id from localStorage
 *   username {string}   — user.email or user.username from localStorage
 *   onClose  {function} — called when the dialog should close
 */
export default function PasskeyRegistration({ userId, username, onClose }) {
    const [passkeys, setPasskeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null); // credentialId being deleted
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const msgTimerRef = useRef(null);
    const [showRegisterForm, setShowRegisterForm] = useState(false);

    const showError = (msg) => {
        clearTimeout(msgTimerRef.current);
        setError(msg);
        setSuccess('');
        msgTimerRef.current = setTimeout(() => setError(''), 4000);
    };

    const showSuccess = (msg) => {
        clearTimeout(msgTimerRef.current);
        setSuccess(msg);
        setError('');
        msgTimerRef.current = setTimeout(() => setSuccess(''), 4000);
    };

    const loadPasskeys = useCallback(async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const list = await getUserPasskeys(userId);
            setPasskeys(list || []);
        } catch {
            showError('Could not load passkeys. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { loadPasskeys(); }, [loadPasskeys]);

    useEffect(() => () => clearTimeout(msgTimerRef.current), []);

    const handleDelete = async (credentialId) => {
        setDeleting(credentialId);
        setError('');
        setSuccess('');
        try {
            const result = await deletePasskey(userId, credentialId);
            if (result?.statusCode === '1' || result?.StatusCode === '1') {
                showSuccess('Passkey removed.');
                setPasskeys(prev => prev.filter(p => p.credentialId !== credentialId));
                setConfirmDeleteId(null);
            } else {
                showError(result?.message || 'Failed to remove passkey.');
            }
        } catch (err) {
            showError(err.message || 'Failed to remove passkey.');
        } finally {
            setDeleting(null);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try { return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
        catch { return dateStr; }
    };

    const isSupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="sm:max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="h-5 w-5 text-primary" />
                            Manage Passkeys
                        </DialogTitle>
                    </DialogHeader>

                    {!isSupported ? (
                        <>
                        <div className="py-6 text-center text-sm text-muted-foreground">
                            <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                            Your browser does not support passkeys. Please use a modern browser such as Chrome, Edge, or Safari.
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        </DialogFooter>
                        </>
                    ) : (
                        <>
                        <div className="space-y-5 py-2">
                            {error && (
                                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 rounded-md px-3 py-2">
                                    {error}
                                </p>
                            )}
                            {success && (
                                <p className="text-sm text-green-600 bg-green-50 dark:bg-green-950/30 rounded-md px-3 py-2">
                                    {success}
                                </p>
                            )}

                            <div className="space-y-2">
                                <p className="text-sm font-medium text-foreground">Registered Passkeys</p>

                                {loading ? (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Loading...
                                    </div>
                                ) : passkeys.length === 0 ? (
                                    <div className="py-4 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
                                        <Monitor className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                                        No passkeys registered yet.
                                        <br />
                                        Register one below to enable passwordless sign-in.
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-border rounded-lg border overflow-hidden">
                                        {passkeys.map((pk) => (
                                            <li key={pk.credentialId}>
                                                <Collapsible
                                                    open={confirmDeleteId === pk.credentialId}
                                                    onOpenChange={(open) => {
                                                        setConfirmDeleteId(open ? pk.credentialId : null);
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between px-4 py-3 bg-card hover:bg-muted/30 transition-colors">
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-foreground truncate">
                                                                {pk.deviceName || 'Unknown Device'}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                Added {formatDate(pk.createdAt)}
                                                                {pk.lastUsedAt ? ` · Last used ${formatDate(pk.lastUsedAt)}` : ''}
                                                            </p>
                                                        </div>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <CollapsibleTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 text-muted-foreground hover:text-red-500 shrink-0 data-[state=open]:text-red-500"
                                                                            disabled={deleting === pk.credentialId}
                                                                        >
                                                                            {deleting === pk.credentialId
                                                                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                                : <Trash2 className="h-4 w-4" />}
                                                                        </Button>
                                                                    </CollapsibleTrigger>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Remove passkey</TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    <CollapsibleContent>
                                                        <div className="px-4 py-3 bg-destructive/5 border-t border-destructive/10">
                                                            <p className="text-sm text-foreground">
                                                                Remove passkey for <span className="font-medium">{pk.deviceName || 'this device'}</span>?
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                You will need to register again to sign in with this device.
                                                            </p>
                                                            <div className="flex justify-end gap-2 mt-3">
                                                                <Button
                                                                    type="button"
                                                                    variant="destructive"
                                                                    size="sm"
                                                                    disabled={deleting === pk.credentialId}
                                                                    onClick={() => handleDelete(pk.credentialId)}
                                                                >
                                                                    {deleting === pk.credentialId ? (
                                                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Removing…</>
                                                                    ) : (
                                                                        'Remove Passkey'
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    disabled={deleting === pk.credentialId}
                                                                    onClick={() => setConfirmDeleteId(null)}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </CollapsibleContent>
                                                </Collapsible>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {showRegisterForm && (
                                <div className="space-y-3 border rounded-lg p-4 bg-muted/20">
                                    <PasskeyRegisterPanel
                                        userId={userId}
                                        username={username}
                                        showExistingDeviceHint={!loading}
                                        submitLabel="Register Passkey"
                                        onCancel={() => { setShowRegisterForm(false); setError(''); }}
                                        cancelLabel="Back"
                                        onSuccess={async () => {
                                            showSuccess('Passkey registered! You can now sign in without a password.');
                                            setShowRegisterForm(false);
                                            await loadPasskeys();
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            {!showRegisterForm && (
                                <Button
                                    type="button"
                                    onClick={() => { setShowRegisterForm(true); setError(''); setSuccess(''); setConfirmDeleteId(null); }}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Register New Passkey
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                                disabled={deleting !== null}
                            >
                                Cancel
                            </Button>
                        </DialogFooter>
                        </>
                    )}
            </DialogContent>
        </Dialog>
    );
}
